// New project from a past event: pick the event, give the new one its name, client and date, and Prism copies the
// budget, sub-budgets, deadlines, crew and on-site schedule (core/copy-project.js says exactly what comes across).
import { h, useState } from '../core/react.js';
import { T } from '../core/theme.js';
import { btnStyle, inputStyle } from '../ui/widgets.js';
import { API } from '../core/api.js';
import { Core } from '../../modules/schedule/core.js';
import { copyProject } from '../core/copy-project.js';
import { dayGap, dayText } from '../core/dates.js';
import { plural } from '../core/format.js';

const COLL = 'schedule/events';

// A copy of an on-site schedule under the id `newId`, its days moved by `shift`, with no crew on it: no people list,
// nobody named as responsible or on a block (the new project's crew is copied into it once it is linked).
async function copySchedule(id, newId, name, shift) {
  const r = await API.call('docGet', { coll: COLL, id });
  const ev = r.exists && Core.fromStore(r.data);
  if (!ev || !ev.days?.length) return false;
  const c = Core.cloneEvent(ev, { name, start: Core.addDays(ev.days[0].date, shift) });
  c.id = newId; c.people = []; c.createdAt = Date.now();
  c.blocks.forEach(b => { b.owner = ''; b.crew = ''; });
  c.days.forEach(d => d.columns.forEach(col => { col.responsible = ''; }));
  await API.call('docSet', { coll: COLL, id: c.id, data: Core.toStore(c), ifVersion: 0 });
  return true;
}

export function CopyProjectPage({ projects, user, onCreated, flashSync }) {
  const past = projects.slice().sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')) || String(a.name).localeCompare(String(b.name)));
  const [srcId, setSrcId] = useState(past[0]?.id || '');
  const src = projects.find(p => p.id === srcId) || null;
  const [form, setForm] = useState(() => ({ name: past[0] ? `${past[0].name} (copy)` : '', client: past[0]?.client || '', date: '' }));
  const [busy, setBusy] = useState(false);
  const readOnly = ['Read-only', 'Freelancer'].includes(user?.role);
  const pick = id => { const p = projects.find(x => x.id === id); setSrcId(id); setForm(f => ({ ...f, name: p ? `${p.name} (copy)` : '', client: p?.client || '' })); };
  const lines = (src?.sections || []).reduce((n, s) => n + (s.items || []).length, 0);
  const schedules = src?.links?.schedule || [];
  const shift = dayGap(src?.date, form.date);
  async function create() {
    if (!src || !form.name.trim()) return;
    setBusy(true);
    try {
      // The project is saved first, already linked to its schedules' new ids, so the server can keep a restricted
      // event's copy restricted and each schedule copy is hidden with it from the moment it exists.
      const name = form.name.trim(), ids = schedules.map(() => 'ev_' + Core.uid() + Core.uid());
      const proj = copyProject(src, { name, client: form.client.trim(), date: form.date, scheduleIds: ids });
      let saved = await API.call('project.save', { id: proj.id, data: proj, baseVersion: null });
      const done = [], failed = [];
      for (let i = 0; i < schedules.length; i++) {
        try { if (await copySchedule(schedules[i], ids[i], name, shift)) done.push(ids[i]); } catch (e) { failed.push(e.message); }
      }
      if (done.length < ids.length) {
        proj.links = done.length ? { schedule: done } : {};
        saved = await API.call('project.save', { id: proj.id, data: proj, baseVersion: saved.version });
      }
      onCreated({ id: proj.id, version: saved.version, data: proj });
      if (failed.length) flashSync(`Created, but the on-site schedule could not be copied: ${failed[0]}`, 'warn');
      else flashSync(`"${form.name.trim()}" created from ${src.name}`);
    } catch (e) { flashSync(e.message, 'err'); setBusy(false); }
  }
  const label = (text, el) => h("label", { style: { display: 'block', marginBottom: 14 } },
    h("div", { style: { fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: T.mist, marginBottom: 5 } }, text), el);
  const field = { ...inputStyle, width: '100%', boxSizing: 'border-box' };
  if (!past.length) return h("div", { style: { padding: 32, fontSize: 13, color: T.slate } }, 'There are no past events to copy yet.');
  return h("div", { style: { flex: 1, overflowY: 'auto', padding: '28px 24px' }, className: 'fade-up' },
    h("div", { style: { maxWidth: 560 } },
      h("div", { style: { fontSize: 16, fontWeight: 600, color: T.coal, marginBottom: 4 } }, 'New project from a past event'),
      h("div", { style: { fontSize: 12.5, color: T.slate, lineHeight: 1.6, marginBottom: 20 } },
        'The budget comes across with its lines, prices and suppliers, every line back to TBC and nothing spent. Sub-budgets, deadlines, the crew list (names and roles) and the on-site schedule come too; dates move to the new event date. Invoices, quotes and files stay with the past event.'),
      label('PAST EVENT', h("select", { value: srcId, onChange: e => pick(e.target.value), 'aria-label': 'Past event', style: field },
        past.map(p => h("option", { key: p.id, value: p.id }, [p.name, p.client, dayText(p.date)].filter(Boolean).join(' · '))))),
      label('NEW PROJECT NAME', h("input", { value: form.name, onChange: e => setForm({ ...form, name: e.target.value }), 'aria-label': 'New project name', style: field })),
      label('CLIENT', h("input", { value: form.client, onChange: e => setForm({ ...form, client: e.target.value }), 'aria-label': 'Client', style: field })),
      label('NEW EVENT DATE', h("input", { type: 'date', value: form.date, onChange: e => setForm({ ...form, date: e.target.value }), 'aria-label': 'New event date', style: field })),
      src && h("div", { style: { background: T.ivory, border: `1px solid ${T.rule}`, padding: '12px 14px', fontSize: 12.5, color: T.slate, lineHeight: 1.7, marginBottom: 18 } },
        h("div", { style: { fontWeight: 600, color: T.coal } }, 'What comes across'),
        h("div", null, `${plural(lines, 'budget line')} in ${plural((src.sections || []).length, 'section')}`),
        (src.subBudgets || []).length > 0 && h("div", null, plural(src.subBudgets.length, 'sub-budget')),
        (src.deadlines || []).length > 0 && h("div", null, plural(src.deadlines.length, 'deadline')),
        (src.crew || []).filter(c => c.name).length > 0 && h("div", null, `${plural(src.crew.filter(c => c.name).length, 'crew member')} (names and roles)`),
        schedules.length > 0 && h("div", null, plural(schedules.length, 'on-site schedule')),
        h("div", { style: { marginTop: 6, color: shift ? T.slate : T.mist } },
          !src.date ? 'The past event has no date, so dates are copied as they were.'
            : !form.date ? 'Pick the new event date to move deadlines and the schedule.'
            : shift === 0 ? 'Same date: dates stay as they were.'
            : `Dates move ${Math.abs(shift)} day${Math.abs(shift) === 1 ? '' : 's'} ${shift > 0 ? 'later' : 'earlier'} (${dayText(src.date)} → ${dayText(form.date)}).`)),
      h("button", { onClick: create, disabled: readOnly || busy || !src || !form.name.trim(), style: { ...btnStyle(true), opacity: readOnly || busy || !form.name.trim() ? .6 : 1 } },
        busy ? 'Creating…' : 'Create the new project')));
}
